import { Button, Card, Col, DatePicker, Form, Row, Spin, Table } from 'antd'
import React, { useEffect, useState } from 'react'
import BaseListing from 'src/BaseListings'
import { wtApiCall, wtApiPostWithContentTypeHandling } from 'src/helpers/commons/ApiWrapper'
import { wtGetFromLocalStorage } from 'src/helpers/commons/LocalStorage'
import { useMasterDateTimeFormats } from 'src/helpers/commons/masterDateFormat'
import { lzNotifyErrorMessageFromResult } from 'src/helpers/commons/notifications'
import { CHECKUP_FOR_SELF } from 'src/models/opd'
import WidgetBranchSelector from 'src/widgets/Selectors/WidgetBranchSelector'
import WidgetEmployeeSelector from 'src/widgets/Selectors/WidgetEmployeeSelector'

const { RangePicker } = DatePicker

export default function OpdReport() {
  const Base = new BaseListing()
  const companyName = wtGetFromLocalStorage('company_name')
  const [form] = Form.useForm()
  const { dateFormat, formatDate, momentDateFormat } = useMasterDateTimeFormats()
  const activeBranchId = Number(wtGetFromLocalStorage('default_branch_id', 0))
  const activeBranchName = wtGetFromLocalStorage('default_branch_name', '')
  const [branchId, setBranchId] = useState(activeBranchId > 0 ? activeBranchId : 0)
  const [branchName, setBranchName] = useState(activeBranchId > 0 ? activeBranchName : '')
  const [dateRange, setDateRange] = useState([])
  const [branches, setBranches] = useState([])
  const [employees, setEmployees] = useState([])
  const [employeeId, setEmployeeId] = useState(0)
  const [employeeName, setEmployeeName] = useState('')
  const [reportData, setReportData] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [totalRecords, setTotalRecords] = useState(0)

  const loadEmployees = (selectedBranchId = branchId) => {
    if (!selectedBranchId) {
      setEmployees([])
      return
    }

    Base.dropdownData('employees_designation', { branch_id: selectedBranchId }, [setEmployees], 1)
  }

  const getReport = (page = 1, size = pageSize) => {
    setIsLoading(true)
    wtApiCall(
      'reports/opd_report',
      {
        from_date: dateRange[0]?.format('YYYY-MM-DD'),
        to_date: dateRange[1]?.format('YYYY-MM-DD'),
        branch_id: branchId || undefined,
        employee_id: employeeId || undefined,
        _page_index: page,
        _page_size: size,
      },
      'GET',
      (response) => {
        setReportData(response.data?.data || [])
        setCurrentPage(response.data?.current_page || page)
        setPageSize(response.data?.per_page || size)
        setTotalRecords(response.data?.total || 0)
        setIsLoading(false)
      },
      (error) => {
        setIsLoading(false)
        lzNotifyErrorMessageFromResult(error)
      },
    )
  }

  useEffect(() => {
    Base.dropdownData('branches', {}, [setBranches], 1)
    loadEmployees()
    getReport()
  }, [])

  const onTableChange = (pagination) => {
    getReport(pagination.current, pagination.pageSize)
  }

  const exportToExcel = () => {
    setIsLoading(true)
    wtApiPostWithContentTypeHandling('reports/opd_report/export', {
      from_date: dateRange[0]?.format('YYYY-MM-DD'),
      to_date: dateRange[1]?.format('YYYY-MM-DD'),
      branch_id: branchId || undefined,
      employee_id: employeeId || undefined,
      date_format: dateFormat,
    }).finally(() => setIsLoading(false))
  }

  const columns = [
    { title: 'Branch', key: 'branch', width: 125, render: (_, record) => record.branch?.name || '-' },
    { title: 'ID', dataIndex: 'employee_id', key: 'employee_id', width: 60 },
    { title: 'Personal File No', key: 'personal_file_no', width: 115, render: (_, record) => record.employee?.personal_file_no || '-' },
    { title: 'PL Number', key: 'pl_number', width: 95, render: (_, record) => record.employee?.pl_number || '-' },
    { title: 'Employee Name', key: 'employee_name', width: 200, render: (_, record) => [record.employee?.first_name, record.employee?.last_name].filter(Boolean).join(' ') || '-' },
    { title: 'Department', key: 'department', width: 170, render: (_, record) => record.employee?.department?.name || '-' },
    { title: 'Designation', key: 'designation', width: 170, render: (_, record) => record.employee?.designation?.name || '-' },
    { title: 'Appointment Date', dataIndex: 'appointment_date', key: 'appointment_date', width: 125, render: (value) => formatDate(value, '-') },
    { title: 'Checkup', key: 'checkup_for', width: 140, render: (_, record) => record.checkup_for === CHECKUP_FOR_SELF ? 'Self' : 'Family Member' },
    {
      title: 'Family Member Name',
      key: 'family_member',
      width: 190,
      render: (_, record) => record.family_members?.name || '-',
    },
    { title: 'Relationship', key: 'relationship', width: 140, render: (_, record) => record.family_members?.relationship || '-' },
  ].map((column) => ({
    ...column,
    onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
  }))

  return (
    <div className="container-fluid" style={{ padding: '1em', backgroundColor: 'white', minHeight: '100vh' }}>
      <Form layout="vertical" form={form} onFinish={() => getReport(1)}>
        <Card
          title={
            <span style={{ fontSize: '22px', display: 'block', textAlign: 'center' }}>
              {companyName} OPD Report
            </span>
          }
        >
          <Row gutter={[16, 16]} align="bottom">
            <Col xs={24} sm={12} md={5}>
              <label>Date Range</label>
              <RangePicker className="w-100" format={momentDateFormat} value={dateRange} onChange={(dates) => setDateRange(dates || [])} />
            </Col>
            <Col xs={24} sm={12} md={5}>
              <label>Branch Name</label>
              <WidgetBranchSelector
                branches={branches.filter((branch) => activeBranchId === -1 || branch.value === activeBranchId)}
                value={branchId}
                displayValue={branchName}
                onChange={(value) => {
                  const selectedBranchId = value?.value || 0
                  setBranchId(selectedBranchId)
                  setBranchName(value?.label || '')
                  setEmployeeId(0)
                  setEmployeeName('')
                  loadEmployees(selectedBranchId)
                }}
                viewOnly={activeBranchId !== -1}
                showCloudIcon={false}
                size="sm"
              />
            </Col>
            <Col xs={24} sm={12} md={5}>
              <label>Employee Name</label>
              <WidgetEmployeeSelector
                employees={employees}
                value={employeeId}
                displayValue={employeeName}
                showCloudIcon={false}
                size="sm"
                onChange={(value) => {
                  setEmployeeId(value?.value || 0)
                  setEmployeeName(value?.label || '')
                }}
              />
            </Col>
            <Col xs={24} sm={24} md={9} style={{ display: 'flex', alignItems: 'end', gap: 8 }}>
              <Button type="primary" htmlType="submit" disabled={isLoading}>
                Generate Report
              </Button>
              {Base.hasRights('View OPD Report') && (
                <Button style={{ backgroundColor: '#4BB543', color: '#fff' }} onClick={exportToExcel} disabled={isLoading}>
                  Export
                </Button>
              )}
            </Col>
          </Row>

          <br />

          <Table
            bordered
            rowKey="id"
            columns={columns}
            dataSource={reportData}
            loading={{ indicator: <Spin />, spinning: isLoading }}
            size="small"
            scroll={{ x: 'max-content', y: 'calc(100vh - 360px)' }}
            pagination={{
              current: currentPage,
              pageSize,
              total: totalRecords,
              showSizeChanger: true,
              pageSizeOptions: [10, 25, 50, 100],
            }}
            onChange={onTableChange}
          />
        </Card>
      </Form>
    </div>
  )
}
